import { Space } from "antd/es";
import { Guid } from "guid-typescript";
import React, { PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { CommonProps } from "../common-props";

interface VerticalStackProps {
  children?: ReactNode;
  style: React.CSSProperties;
  size?: number | string;
}

interface VerticalStackState {}

class VerticalStack extends PureComponent<VerticalStackProps & CommonProps, VerticalStackState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`vstack_${this.uniqueKey}`);

  private readonly borderProperties = [
    "borderRadius",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomLeftRadius",
    "borderBottomRightRadius",
    "borderWidth",
    "borderLeftWidth",
    "borderRightWidth",
    "borderTopWidth",
    "borderBottomWidth",
    "borderColor",
    "border",
    "borderStyle"
  ] as const;

  constructor(props: VerticalStackProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.setDynamicStyle();
  }

  componentDidUpdate(prevProps: VerticalStackProps) {
    if (!isEqual(prevProps.style, this.props.style) || !isEqual(prevProps.size, this.props.size)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  private extractBorderProperties = (sourceStyle: React.CSSProperties): React.CSSProperties => {
    const borderStyles: React.CSSProperties = {};

    this.borderProperties.forEach((property) => {
      const value = sourceStyle[property as keyof React.CSSProperties];
      if (value !== undefined) {
        (borderStyles as any)[property] = value;
      }
    });

    return borderStyles;
  };

  private removeBorderProperties = (style: React.CSSProperties): React.CSSProperties => {
    const cleanedStyle = { ...style };

    this.borderProperties.forEach((property) => {
      if (cleanedStyle[property as keyof React.CSSProperties] !== undefined) {
        delete (cleanedStyle as any)[property];
      }
    });

    return cleanedStyle;
  };

  getProps = () => {
    const props: any = { ...this.props };
    if (props.children) {
      delete props.children;
    }
    if (props.style) {
      delete props.style;
    }
    if (props.size < 0) props.size = 0;
    return props;
  };

  getStyleProp = () => {
    let style: React.CSSProperties = {};

    if (this.props.style) {
      style = this.removeBorderProperties(this.props.style);
    }

    style.height = "auto";
    style.width = "100%";

    return style;
  };

  getContainerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      overflowX: "hidden",
      width: "100%"
    };

    const borderStyles = this.extractBorderProperties(this.props.style || {});
    const containerStyle = { ...baseStyle, ...borderStyles };

    return containerStyle;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getUniqueClassName = () => {
    return `k_vstack_${this.uniqueKey.toString().substring(0, 8)}`;
  };

  getDynamicCss = (): string => {
    const className = this.getUniqueClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    const { size } = this.props;

    if ((size && typeof size === "number" && size > 0) || (typeof size === "string" && parseInt(size) > 0)) {
      result += `.${className}>.ant-space-item:not(:first-of-type) {
          margin-top: ${size}px !important;
        }`;
    }

    return result;
  };

  getClassName = () => {
    let result = this.getUniqueClassName();

    if (this.props.className) {
      result = `${this.props.className} ${result}`;
    }

    return result.trim();
  };

  render(): ReactNode {
    return (
      <div style={this.getContainerStyle()} className={this.getUniqueClassName()}>
        <Space className={this.getClassName()} style={this.getStyleProp()} {...this.getProps()} direction="vertical">
          {React.Children.map(this.props.children, (child: any) => {
            if (child?.props?.visibility === "hidden" || child?.props?.children?.props?.visibility === "hidden") {
              return null;
            }
            return <>{child}</>;
          })}
        </Space>
      </div>
    );
  }
}

const verticalStack = withCommonEvents(VerticalStack);
export { verticalStack as VerticalStack };
